import { Box, useTheme } from "@mui/material";
import React, { ReactNode, useCallback, useRef, useEffect } from "react";

interface ResizablePanelProps {
  children: ReactNode;
  width: number;
  height?: string | number;
  onResize: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  /** Show resize handle on left edge (default true) */
  handlePosition?: "left" | "right";
  /** Callback when drag starts */
  onDragStart?: () => void;
  /** Callback when drag ends */
  onDragEnd?: () => void;
  sx?: any;
}

/**
 * ResizablePanel component that allows dragging to resize width.
 * Useful for side panels and preview panes that need flexible sizing.
 *
 * Usage:
 * <ResizablePanel
 *   width={panelWidth}
 *   onResize={setPanelWidth}
 *   minWidth={200}
 *   maxWidth={800}
 * >
 *   <Content />
 * </ResizablePanel>
 */
const ResizablePanel = ({
  children,
  width,
  height = "100%",
  onResize,
  minWidth = 200,
  maxWidth = Number.POSITIVE_INFINITY,
  handlePosition = "left",
  onDragStart,
  onDragEnd,
  sx,
}: ResizablePanelProps) => {
  const theme = useTheme();
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef<number | null>(null);

  // Use refs to store stable function references for addEventListener/removeEventListener
  // This is a justified use of refs to prevent stale closures and ensure proper cleanup
  const onPointerMoveRef = useRef<(e: PointerEvent) => void>();
  const endDragRef = useRef<() => void>();

  // Update refs with latest implementations (capturing current props/state)
  useEffect(() => {
    onPointerMoveRef.current = (e: PointerEvent) => {
      if (!isDraggingRef.current) return;
      if (startWidthRef.current == null) return;

      const delta =
        handlePosition === "left"
          ? startXRef.current - e.clientX
          : e.clientX - startXRef.current;

      const viewportMax = window.innerWidth;
      const dynamicMax = Math.min(maxWidth, viewportMax);
      let newWidth = startWidthRef.current + delta;
      newWidth = Math.max(minWidth ?? 200, Math.min(dynamicMax, newWidth));

      onResize(newWidth);
    };

    endDragRef.current = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.cursor = "";
      }
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handleEndDrag);
      window.removeEventListener("pointercancel", handleEndDrag);
      onDragEnd?.();
    };
  });

  // Stable function references for addEventListener/removeEventListener
  const handlePointerMove = useCallback((e: PointerEvent) => {
    onPointerMoveRef.current?.(e);
  }, []);

  const handleEndDrag = useCallback(() => {
    endDragRef.current?.();
  }, []);

  // Pointer events (not mouse events) so touch/pen drags work: iOS Safari
  // never synthesizes mousemove for a finger drag, only for taps.
  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      document.body.style.cursor = "col-resize";
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handleEndDrag);
      window.addEventListener("pointercancel", handleEndDrag);
      e.preventDefault();
      e.stopPropagation();
      onDragStart?.();
    },
    [width, handlePointerMove, handleEndDrag, onDragStart]
  );

  const handleSide = handlePosition === "left" ? "left" : "right";

  // Cleanup listeners if component unmounts while dragging
  // Now uses stable function references, so cleanup only runs on unmount
  useEffect(() => {
    return () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.cursor = "";
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handleEndDrag);
        window.removeEventListener("pointercancel", handleEndDrag);
      }
    };
  }, [handlePointerMove, handleEndDrag]);

  return (
    <Box
      sx={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
    >
      {/* Resize Handle */}
      <Box
        onPointerDown={startDrag}
        aria-label="Resize panel"
        sx={{
          position: "absolute",
          [handleSide]: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          backgroundColor: "transparent",
          cursor: "col-resize",
          flexShrink: 0,
          zIndex: 10,
          // Keep the browser from turning a drag into page scroll / swipe-back
          touchAction: "none",
          "&::after": {
            content: '""',
            position: "absolute",
            [handleSide]: "1px",
            top: 0,
            bottom: 0,
            width: "2px",
            backgroundColor: theme.palette.grey[300],
            borderRadius: "1px",
            opacity: 0.7,
            transition: "all 150ms ease-out",
          },
          "&:hover::after": {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
            width: "3px",
          },
          // A 4px strip is fine for a cursor but untouchable with a fingertip;
          // widen the hit area on touch devices. The visible 2px indicator
          // (::after) stays anchored to the panel edge. Kept inside the panel
          // because the wrapper is overflow:hidden. Must come last in sx so it
          // wins over the base rules above.
          "@media (pointer: coarse)": {
            width: "16px",
          },
        }}
      />

      {/* Content */}
      {children}
    </Box>
  );
};

export default ResizablePanel;
